import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiShoppingBag,
  FiShoppingCart,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiDownload,
} from "react-icons/fi";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import ApprovalEntity, {
  ApprovalEntityFooter,
  ApprovalEntityRowActions,
} from "../../../components/CustomUI/ApprovalEntity";
import { formatInstantDateShortTr } from "../../../utils/appTime";
import { limitWords } from "../../../utils/textDisplay";
import OrderDetailBody from "../../../approvals/plugins/order/OrderDetailBody";
import OrderDetailHeader from "../../../approvals/plugins/order/OrderDetailHeader";
// Shared price display: follows the system USD/TL toggle in the header
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import {
  getOrderStatusDisplay,
  hasOrderSale,
  canMakeSaleFromOrder,
  inferOrderDiscountScope,
  clampPctView,
  orderLineDisplayAmount,
  formatOrderLineDiscount,
  orderHasDiscountBlock,
  getRejectReasonFromLogs,
} from "../../../approvals/plugins/order/orderDetailUtils";
import "../../../approvals/plugins/order/orderDetail.css";
import { generateOfferPDF } from "../../../utils/generateOfferPDF";
import PdfPreviewModal from "../../../components/CustomUI/PdfPreviewModal";
import { usePdfPreview } from "../../../hooks/usePdfPreview";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import { buildRadioFilterSection } from "../shared/buildListFilterSections";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const getOrderCreatorName = (order) =>
  order?.createdBy?.name ||
  order?.logs?.[0]?.user?.name ||
  order?.logs?.[0]?.createdBy?.name ||
  "-";

const getOrderCreatorEmail = (order) =>
  order?.createdBy?.email ||
  order?.logs?.[0]?.user?.email ||
  order?.logs?.[0]?.createdBy?.email ||
  undefined;

const Orders = () => {
  const fx = useExchangeRateOptional();
  const [orders, setOrders] = useState([]);

  const parseRateNum = (val) => {
    if (val === undefined || val === null || val === "") return null;
    const num = Number(String(val).trim().replace(",", "."));
    return !isNaN(num) && num > 0 ? num : null;
  };
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);

  const [viewOrder, setViewOrder] = useState(null);
  const [modelDetailsOrder, setModelDetailsOrder] = useState(null);
  const {
    pdfPreview,
    closePdfPreview,
    openPdfPreview,
    downloadPdfFromPreview,
  } = usePdfPreview();

  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const format = useFormatValue;
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/orders",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const breadcrumbs = [{ label: "Rovion", url: "" }, { label: "Teklifler" }];

  const addToast = (description, appearance = "success") => {
    setToasts([
      {
        description,
        id: Date.now(),
        title: appearance === "success" ? "Başarılı!" : "Hata!",
        appearance,
      },
    ]);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setOrders(Array.isArray(res.data?.orders) ? res.data.orders : []);
    } catch (err) {
      setToasts([
        {
          description: err.response?.data?.message || "Teklifler yüklenemedi",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (order) => {
    if (!order?._id) return;
    try {
      setActionLoading(order._id + "_delete");
      await axios.delete(`${API_URL}/api/orders/${order._id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setToasts([
        {
          description: "Teklif silindi",
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);
      fetchOrders();
    } catch (err) {
      setToasts([
        {
          description: err.response?.data?.message || "Silme başarısız",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setActionLoading(null);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const filteredOrders = orders.filter(
    (o) => filterStatus === "all" || o.status === filterStatus,
  );

  const headers = [
    {
      id: 0,
      name: "Teklif Adı",
      dataType: "text",
      customSorter: (a, b) =>
        String(a.orderName || "").localeCompare(
          String(b.orderName || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      id: 1,
      name: "Firma",
      dataType: "text",
      customSorter: (a, b) =>
        String(a.companyId?.name || "").localeCompare(
          String(b.companyId?.name || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      id: 2,
      name: "Modeller",
      dataType: "number",
      customSorter: (a, b) => (a.models || []).length - (b.models || []).length,
    },
    {
      id: 3,
      name: "Toplam Tutar",
      dataType: "number",
      customSorter: (a, b) =>
        (Number(a.totalAmount) || 0) - (Number(b.totalAmount) || 0),
    },
    {
      id: 8,
      name: translate("UI.EXCHANGE_RATE") || "Döviz Kuru",
      dataType: "number",
      customSorter: (a, b) => {
        const rateA = parseRateNum(a.exchangeRate) ?? parseRateNum(fx?.rate) ?? 0;
        const rateB = parseRateNum(b.exchangeRate) ?? parseRateNum(fx?.rate) ?? 0;
        return rateA - rateB;
      },
    },
    {
      id: 4,
      name: "Tarih",
      dataType: "date",
      customSorter: (a, b) => {
        const ta = new Date(a?.orderDate).getTime();
        const tb = new Date(b?.orderDate).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    {
      id: 5,
      name: "Teklifi Oluşturan",
      dataType: "text",
      customSorter: (a, b) =>
        getOrderCreatorName(a).localeCompare(getOrderCreatorName(b), "tr"),
    },
    {
      id: 6,
      name: "Durum",
      dataType: "text",
      customSorter: (a, b) =>
        getOrderStatusDisplay(a).label.localeCompare(
          getOrderStatusDisplay(b).label,
          "tr",
        ),
    },
    { id: 7, name: "İşlemler", sortable: false },
  ];

  const {
    visibleColumns,
    toggleColumnVisibility,
    resetToDefault,
  } = useColumnVisibility("orders-list", headers);

  const activeFilterCount = filterStatus !== "all" ? 1 : 0;

  const clearFilters = useCallback(() => {
    setFilterStatus("all");
  }, []);

  const orderFilterSections = useMemo(
    () => [
      buildRadioFilterSection({
        id: "status",
        title: "Durum",
        value: filterStatus,
        onChange: (v) => setFilterStatus(String(v ?? "all")),
        options: [
          { value: "all", label: "Tümü" },
          { value: "pending", label: "Bekliyor" },
          { value: "accepted", label: "Onaylandı" },
          { value: "rejected", label: "Reddedildi" },
        ],
      }),
    ],
    [filterStatus],
  );

  const viewOptionsPanel = (
    <ListViewOptionsPanel
      columns={headers}
      visibleColumns={visibleColumns}
      onToggleColumn={toggleColumnVisibility}
      onResetColumns={resetToDefault}
      filterSections={orderFilterSections}
      activeFilterCount={activeFilterCount}
      onReset={clearFilters}
      showApply={false}
    />
  );

  const stats = {
    total: orders.length,
    adminPending: orders.filter(
      (o) =>
        o.currentApproval?.status === "pending" &&
        o.currentApproval?.stage === "admin",
    ).length,
    userPending: orders.filter(
      (o) =>
        o.currentApproval?.status === "pending" &&
        o.currentApproval?.stage === "user",
    ).length,
    acceptedReady: orders.filter((o) => canMakeSaleFromOrder(o)).length,
    acceptedWithSale: orders.filter((o) => hasOrderSale(o)).length,
    rejected: orders.filter((o) => o.status === "rejected").length,
  };

  const handleMakeSale = async (order) => {
    if (!order?._id) return;
    try {
      setActionLoading(order._id + "_makesale");
      const res = await axios.post(
        `${API_URL}/api/orders/${order._id}/make-sale`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      addToast("Sipariş başarıyla oluşturuldu");
      fetchOrders();
      const saleId = res.data?.saleId;
      if (saleId) navigate(`/device-sales/edit/${saleId}`);
    } catch (err) {
      addToast(
        err.response?.data?.message || "Sipariş oluşturulamadı",
        "danger",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const confirmMakeSale = (order) => {
    if (!order?._id) return;
    const orderLabel = order.orderName || "Bu teklif";
    const actionLabel =
      order.currentApproval?.approveActionLabel || "Sipariş Oluştur";
    confirm({
      title: `${actionLabel}?`,
      message: `${orderLabel} için sipariş kaydı oluşturulacak ve düzenleme ekranına yönlendirileceksiniz. Devam etmek istiyor musunuz?`,
      appearance: "primary",
      confirmButtonText: actionLabel,
      cancelButtonText: "İptal",
      onConfirm: () => handleMakeSale(order),
    });
  };

  const handlePreviewOfferPdf = (order) => {
    if (!order) return;
    openPdfPreview({
      title: order.orderName
        ? `Teklif PDF — ${order.orderName}`
        : "Teklif PDF Önizleme",
      generate: (currencyOptions) =>
        generateOfferPDF(order, {
          action: "blob",
          ...currencyOptions,
          exchangeRate:
            parseRateNum(order.exchangeRate) ?? currencyOptions?.exchangeRate,
        }),
    });
  };

  const handleRecreateOrder = (order) => {
    if (!order?._id) return;
    navigate(`/orders/create?recreateFrom=${order._id}`);
  };

  return (
    <ApprovalEntity
      type="order"
      stage="user"
      canWrite={canWrite}
      onNotify={addToast}
      onAfterSuccess={() => {
        fetchOrders();
        setViewOrder(null);
      }}
      getSubjectLabel={(item) => item?.orderName}
      rejectVariant="simple"
      reasonPlaceholder="Red nedenini girin..."
    >
      {(approval) => {
        const wrappedApproval = {
          ...approval,
          approve: (item) => {
            if (
              item?.allowFileUpload ||
              item?.currentApproval?.allowFileUpload
            ) {
              approval.approve(item);
              return;
            }
            confirm({
              title: "Onay",
              message: `${item?.orderName || "Bu teklifi"} onaylamak istediğinize emin misiniz?`,
              appearance: "primary",
              confirmButtonText: "Onayla",
              cancelButtonText: "İptal",
              onConfirm: () => approval.approve(item),
            });
          },
          startReject: (item) => {
            confirm({
              title: "Reddet",
              message: `${item?.orderName || "Bu teklifi"} reddetmek istediğinize emin misiniz?`,
              appearance: "danger",
              confirmButtonText: "Devam",
              cancelButtonText: "İptal",
              onConfirm: () => approval.startReject(item),
            });
          },
        };

        return (
          <AppPage
            title="Teklifler"
            breadcrumbs={breadcrumbs}
            actions={[
              <Button
                key="new-order"
                appearance="primary-animated"
                icon={<FiPlus />}
                onClick={() => navigate("/orders/create")}
              >
                Yeni Teklif
              </Button>,
            ]}
          >
            <Container>
              <ContainerItem>
                <>
                  {/* Stats */}
                  <div className="orders-stats">
                    {[
                      { label: "Toplam", value: stats.total, color: "#00b4f3" },
                      {
                        label: "Akış Onayı Bekliyor",
                        value: stats.adminPending,
                        color: "#f59e0b",
                      },
                      {
                        label: "Kullanıcı Onayı Bekliyor",
                        value: stats.userPending,
                        color: "#8b5cf6",
                      },
                      {
                        label: "Sipariş Yapılabilir",
                        value: stats.acceptedReady,
                        color: "#10b981",
                      },
                      {
                        label: "Sipariş Oluşturuldu",
                        value: stats.acceptedWithSale,
                        color: "#059669",
                      },
                      {
                        label: "Reddedildi",
                        value: stats.rejected,
                        color: "#ef4444",
                      },
                    ].map((s) => (
                      <div key={s.label} className="stat-card">
                        <div className="stat-value" style={{ color: s.color }}>
                          {s.value}
                        </div>
                        <div className="stat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Table */}
                  <div className="orders-table-wrapper">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <Table
                          columns={headers}
                          visibleColumns={visibleColumns}
                          tableId="orders-list"
                          loading={loading}
                          search={true}
                          viewOptions={viewOptionsPanel}
                        >
                          {filteredOrders.map((order) => (
                            <Tr key={order._id} id={order._id} data={order}>
                              {visibleColumns.includes(0) && (
                                <Td>
                                  <div
                                    className="order-name"
                                    title={order.orderName || undefined}
                                  >
                                    {limitWords(order.orderName)}
                                  </div>
                                </Td>
                              )}
                              {visibleColumns.includes(1) && (
                                <Td>
                                  <span
                                    title={order.companyId?.name || undefined}
                                  >
                                    {limitWords(order.companyId?.name)}
                                  </span>
                                </Td>
                              )}
                              {visibleColumns.includes(2) && (
                                <Td>
                                  <div
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setModelDetailsOrder(order);
                                      }
                                    }}
                                  >
                                    <Lozenge appearance="info-subtle">
                                      {(order.models || []).length} model
                                    </Lozenge>
                                  </div>
                                </Td>
                              )}
                              {visibleColumns.includes(3) && (
                                <Td>
                                  <span className="fw-semibold text-primary">
                                    <ProductPrice
                                      value={order.totalAmount || 0}
                                      rate={order.exchangeRate}
                                      showFallbackMark
                                    />
                                  </span>
                                </Td>
                              )}
                              {visibleColumns.includes(8) && (
                                <Td>
                                  {parseRateNum(order.exchangeRate) ? (
                                    <span
                                      className="fw-semibold text-dark"
                                      title="Teklif anında kaydedilen kur"
                                    >
                                      {parseRateNum(order.exchangeRate).toLocaleString(
                                        "tr-TR",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 4,
                                        },
                                      )}{" "}
                                      ₺
                                    </span>
                                  ) : (
                                    <span
                                      className="text-muted"
                                      title="Kendi kuru bulunamadığı için güncel kur gösterilmektedir"
                                    >
                                      {parseRateNum(fx?.rate)
                                        ? `${parseRateNum(fx.rate).toLocaleString("tr-TR", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 4,
                                          })} ₺`
                                        : "-"}
                                      <span
                                        className="text-warning fw-bold ms-1"
                                        title="Güncel kur ile hesaplandı (Teklife ait kur kaydı bulunamadı)"
                                        style={{ cursor: "help", fontSize: "1.05em" }}
                                      >
                                        *
                                      </span>
                                    </span>
                                  )}
                                </Td>
                              )}
                              {visibleColumns.includes(4) && (
                                <Td>
                                  <span className="small">
                                    {order.orderDate
                                      ? formatInstantDateShortTr(
                                          order.orderDate,
                                        )
                                      : "-"}
                                  </span>
                                </Td>
                              )}
                              {visibleColumns.includes(5) && (
                                <Td>
                                  <span
                                    className="small"
                                    title={getOrderCreatorEmail(order)}
                                  >
                                    {getOrderCreatorName(order)}
                                  </span>
                                </Td>
                              )}
                              {visibleColumns.includes(6) && (
                                <Td>
                                  {(() => {
                                    const statusDisplay =
                                      getOrderStatusDisplay(order);
                                    const saleId =
                                      typeof order.saleId === "string"
                                        ? order.saleId
                                        : order.saleId?._id;
                                    const badgeTitle =
                                      order.status === "rejected"
                                        ? getRejectReasonFromLogs(order) ||
                                          "Reddedildi"
                                        : hasOrderSale(order)
                                          ? "Bu teklif siparişa dönüştürüldü"
                                          : canMakeSaleFromOrder(order)
                                            ? "Bu tekliften sipariş oluşturulabilir"
                                            : undefined;

                                    return (
                                      <div className="order-status-cell">
                                        <Lozenge
                                          appearance={statusDisplay.appearance}
                                          title={badgeTitle}
                                        >
                                          {statusDisplay.label}
                                        </Lozenge>
                                        {hasOrderSale(order) && saleId && (
                                          <button
                                            type="button"
                                            className="order-sale-nav-link"
                                            onClick={() =>
                                              navigate(
                                                `/device-sales/edit/${saleId}`,
                                              )
                                            }
                                          >
                                            Siparişa git →
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </Td>
                              )}
                              {visibleColumns.includes(7) && (
                                <Td>
                                  <div className="action-buttons d-flex align-items-center">
                                  <ApprovalEntityRowActions
                                    actions={wrappedApproval}
                                    item={order}
                                    canWrite={canWrite}
                                    actionsPosition="before"
                                    onView={() => setViewOrder(order)}
                                  />
                                  <FiDownload
                                    className="cursor-pointer text-grey"
                                    style={{
                                      fontSize: "20px",
                                      cursor: "pointer",
                                    }}
                                    title="PDF Önizle"
                                    onClick={() => handlePreviewOfferPdf(order)}
                                  />
                                  {canWrite && canMakeSaleFromOrder(order) && (
                                    <FiShoppingCart
                                      className="cursor-pointer text-grey"
                                      style={{
                                        fontSize: "20px",
                                        cursor:
                                          actionLoading ===
                                          order._id + "_makesale"
                                            ? "wait"
                                            : "pointer",
                                        opacity:
                                          actionLoading ===
                                          order._id + "_makesale"
                                            ? 0.5
                                            : 1,
                                        pointerEvents:
                                          actionLoading ===
                                          order._id + "_makesale"
                                            ? "none"
                                            : "auto",
                                      }}
                                      title={
                                        order.currentApproval
                                          ?.approveActionLabel ||
                                        "Sipariş Oluştur"
                                      }
                                      onClick={() => confirmMakeSale(order)}
                                    />
                                  )}
                                  {canWrite && order.status === "pending" && (
                                    <FiEdit2
                                      className="cursor-pointer text-grey"
                                      style={{
                                        fontSize: "20px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        navigate(`/orders/edit/${order._id}`)
                                      }
                                      title="Düzenle"
                                    />
                                  )}
                                  {canWrite && order.status === "rejected" && (
                                    <FiRefreshCw
                                      className="cursor-pointer text-grey"
                                      style={{
                                        fontSize: "20px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => handleRecreateOrder(order)}
                                      title="Yeniden Oluştur"
                                    />
                                  )}
                                  {canWrite &&
                                    currentUser?.role === "admin" && (
                                      <FiTrash2
                                        className="cursor-pointer text-grey"
                                        style={{
                                          fontSize: "20px",
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          confirm({
                                            title: "Teklifi sil?",
                                            message:
                                              `${order?.orderName || ""} Teklifini kalıcı olarak silmek istiyor musunuz?`.trim(),
                                            appearance: "danger",
                                            confirmButtonText: "Sil",
                                            cancelButtonText: "İptal",
                                            onConfirm: async () => {
                                              await handleDelete(order);
                                            },
                                          })
                                        }
                                        title="Sil"
                                      />
                                    )}
                                  </div>
                                </Td>
                              )}
                            </Tr>
                          ))}
                        </Table>
                        {filteredOrders.some(
                          (o) => !parseRateNum(o.exchangeRate),
                        ) && (
                          <div className="text-muted small mt-2 d-flex align-items-center gap-1">
                            <span className="text-warning fw-bold">*</span>
                            <span>
                              {translate("UI.FALLBACK_EXCHANGE_RATE_NOTE") ||
                                "İşaretli tutarlar ve kurlar, teklife ait kur kaydı bulunmadığı için güncel kur ile hesaplanmıştır."}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              </ContainerItem>
            </Container>

            {/* View Order Modal */}
            <Modal
              isOpen={!!viewOrder}
              onClose={() => setViewOrder(null)}
              width="x-large"
              scrollBehavior="inside"
            >
              <ModalHeader>
                <OrderDetailHeader orderName={viewOrder?.orderName} />
              </ModalHeader>
              <ModalBody>
                {viewOrder && (
                  <OrderDetailBody
                    order={viewOrder}
                    format={format}
                    onNavigateToSale={(saleId) => {
                      setViewOrder(null);
                      navigate(`/device-sales/edit/${saleId}`);
                    }}
                  />
                )}
              </ModalBody>
              <ModalFooter>
                <div className="d-flex gap-2">
                  <ApprovalEntityFooter
                    actions={wrappedApproval}
                    item={viewOrder}
                    canWrite={canWrite}
                    onReject={() => setViewOrder(null)}
                  />
                  <Button
                    appearance="primary-subtle"
                    icon={<FiDownload />}
                    onClick={() => handlePreviewOfferPdf(viewOrder)}
                  >
                    PDF Önizle
                  </Button>
                  {canWrite && canMakeSaleFromOrder(viewOrder) && (
                    <Button
                      appearance="primary-subtle"
                      icon={<FiShoppingCart />}
                      onClick={() => confirmMakeSale(viewOrder)}
                      loading={actionLoading === viewOrder._id + "_makesale"}
                    >
                      {viewOrder.currentApproval?.approveActionLabel ||
                        "Sipariş Oluştur"}
                    </Button>
                  )}
                  {canWrite && viewOrder?.status === "rejected" && (
                    <Button
                      appearance="primary-subtle"
                      icon={<FiRefreshCw />}
                      onClick={() => {
                        setViewOrder(null);
                        handleRecreateOrder(viewOrder);
                      }}
                    >
                      Yeniden Oluştur
                    </Button>
                  )}
                  <Button
                    appearance="danger-subtle"
                    onClick={() => setViewOrder(null)}
                  >
                    Kapat
                  </Button>
                </div>
              </ModalFooter>
            </Modal>

            {/* Model Details Modal */}
            <Modal
              isOpen={!!modelDetailsOrder}
              onClose={() => setModelDetailsOrder(null)}
              width="x-large"
              scrollBehavior="inside"
            >
              <ModalHeader>
                <div className="d-flex align-items-center gap-2">
                  <div className="order-detail-modal-icon-box">
                    <FiShoppingBag />
                  </div>
                  <div>
                    <h5 className="mb-0">Model Detayları</h5>
                    <small className="text-muted">
                      {modelDetailsOrder?.orderName || "-"}
                    </small>
                  </div>
                </div>
              </ModalHeader>
              <ModalBody>
                {modelDetailsOrder && (
                  <>
                    <div className="order-detail">
                      {orderHasDiscountBlock(modelDetailsOrder) && (
                        <div className="order-detail-grid mb-3">
                          <div className="detail-row">
                            <span className="detail-label">
                              İskonto kapsamı
                            </span>
                            <span>
                              {inferOrderDiscountScope(modelDetailsOrder) ===
                              "order_total"
                                ? "Genel toplam (liste üzerinden)"
                                : "Model bazlı (%)"}
                            </span>
                          </div>
                          {inferOrderDiscountScope(modelDetailsOrder) ===
                            "order_total" && (
                            <div className="detail-row">
                              <span className="detail-label">
                                Genel iskonto
                              </span>
                              <span>
                                {Number(
                                  modelDetailsOrder.orderDiscountPercent,
                                ) > 0 ? (
                                  `%${clampPctView(
                                    modelDetailsOrder.orderDiscountPercent,
                                  )}`
                                ) : Number(
                                    modelDetailsOrder.orderDiscountFixed,
                                  ) > 0 ? (
                                  <ProductPrice
                                    value={modelDetailsOrder.orderDiscountFixed}
                                    rate={modelDetailsOrder.exchangeRate}
                                    showFallbackMark
                                  />
                                ) : (
                                  "—"
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      <Table
                        columns={[
                          { id: 0, name: "Kod", dataType: "text" },
                          { id: 1, name: "Model", dataType: "text" },
                          { id: 2, name: "Adet", dataType: "number" },
                          { id: 3, name: "Birim Fiyat", dataType: "number" },
                          {
                            id: 4,
                            name:
                              inferOrderDiscountScope(modelDetailsOrder) ===
                              "order_total"
                                ? "İskonto"
                                : "İskonto %",
                            dataType: "number",
                          },
                          { id: 5, name: "Satır tutarı", dataType: "number" },
                        ]}
                      >
                        {(modelDetailsOrder.models || []).map((m, i) => (
                          <Tr key={i} id={i} data={m}>
                            <Td>
                              <span className="fw-semibold">
                                {m.modelId?.code || "-"}
                              </span>
                            </Td>
                            <Td>{m.modelId?.name || "-"}</Td>
                            <Td>{m.count || 0}</Td>
                            <Td>
                              <span className="text-muted">
                                <ProductPrice
                                  value={m.modelId?.price || 0}
                                  rate={modelDetailsOrder.exchangeRate}
                                  showFallbackMark
                                />
                              </span>
                            </Td>
                            <Td>
                              <span className="text-muted">
                                {formatOrderLineDiscount(
                                  m,
                                  modelDetailsOrder,
                                  format,
                                )}
                              </span>
                            </Td>
                            <Td>
                              <span className="text-primary fw-semibold">
                                <ProductPrice
                                  value={orderLineDisplayAmount(
                                    m,
                                    modelDetailsOrder,
                                  )}
                                  rate={modelDetailsOrder.exchangeRate}
                                  showFallbackMark
                                />
                              </span>
                            </Td>
                          </Tr>
                        ))}
                      </Table>

                      <div className="detail-total">
                        <span className="fw-bold">Toplam Tutar</span>
                        <span className="fw-bold text-primary fs-5">
                          <ProductPrice
                            value={modelDetailsOrder.totalAmount || 0}
                            rate={modelDetailsOrder.exchangeRate}
                            showFallbackMark
                          />
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  appearance="danger-subtle"
                  onClick={() => setModelDetailsOrder(null)}
                >
                  Kapat
                </Button>
              </ModalFooter>
            </Modal>

            <PdfPreviewModal
              isOpen={pdfPreview.open}
              onClose={closePdfPreview}
              title={pdfPreview.title}
              previewUrl={pdfPreview.url}
              fileName={pdfPreview.fileName}
              loading={pdfPreview.loading}
              error={pdfPreview.error}
              onDownload={downloadPdfFromPreview}
            />

            <ToastContainer>
              {toasts?.map((toast, index) => (
                <Toast
                  {...toast}
                  key={index}
                  onDismissed={() => removeToast(index)}
                  onClose={() => removeToast(index)}
                />
              ))}
            </ToastContainer>

            <style>{`
        .orders-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .orders-stats {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }
        .stat-card {
          flex: 1;
          min-width: 120px;
          background: var(--m-card-background-color, #fff);
          border: 1px solid var(--m-border, #e9ecef);
          border-radius: 12px;
          padding: 1rem 1.25rem;
          text-align: center;
          box-shadow: var(--m-box-shadow, 0 2px 8px rgba(0,0,0,0.04));
        }
        .stat-value { font-size: 2rem; font-weight: 700; line-height: 1; }
        .stat-label { font-size: 0.8rem; color: var(--m-text-light, #6c757d); margin-top: 0.35rem; }
        .orders-filters {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.25rem;
          flex-wrap: wrap;
        }
        .filter-search { flex: 1; min-width: 200px; }
        .filter-tabs { display: flex; gap: 0.35rem; flex-wrap: wrap; }
        .filter-tab {
          border: 1px solid var(--m-border, #e9ecef);
          background: var(--m-card-background-color, #fff);
          border-radius: 20px;
          padding: 0.3rem 0.85rem;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s;
          color: var(--m-text-light, #495057);
        }
        .filter-tab:hover { border-color: var(--bs-primary, #00b4f3); color: var(--bs-primary, #00b4f3); }
        .filter-tab.active { background: var(--bs-primary, #00b4f3); color: #fff; border-color: var(--bs-primary, #00b4f3); font-weight: 600; }
        .orders-table-wrapper { }
        .order-name { font-weight: 600; color: var(--m-text, #1a202c); }
        .models-chips { display: flex; gap: 0.35rem; flex-wrap: wrap; }
        .models-chips-clickable { cursor: pointer; }
        .models-chips-clickable:hover { opacity: 0.9; }
        .model-chip {
          background: var(--m-surface-subtle, #e9ecef);
          color: var(--m-text-light, #495057);
          border-radius: 6px;
          padding: 0.15rem 0.5rem;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .model-chip-more { background: var(--m-border, #dee2e6); color: var(--m-text-light, #6c757d); }
        .action-buttons { display: flex; gap: 0.4rem; align-items: center; flex-wrap: nowrap; }
        .orders-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 200px;
        }
        .orders-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          color: var(--m-text-light, #94a3b8);
        }
        @media (max-width: 768px) {
          .action-buttons { gap: 0.25rem; }
        }
      `}</style>
          </AppPage>
        );
      }}
    </ApprovalEntity>
  );
};

export default Orders;
